/**
 * @copyright 2023 Honda Motor Co., Ltd. All rights reserved.
 */

import { Dialog } from "@mui/material";
import { FC, useCallback, useMemo, useState, useEffect } from "react";
import { DetailArea, DialogContainer, MapArea } from "./Util";
import { useUserContext } from "context/UserContext";
import { Map } from "components/Map/Map";
import { ResetMapPosition } from "components/Map/ResetMapPosition";
import { ResetMapHeading } from "components/Map/ResetMapHeading";
import { NodeMapOverlay } from "../Map/NodeMapOverlay";
import { useMapDataContext } from "context/MapDataProvider";
import { AutoPositioningCircleMapOverlay } from "components/Map/AutoPositioningCircleMapOverlay";
import { AwvInfo, MapNodeType, ResultTaskList, TasksApi } from "client";
import { useApiWithTimeout } from "hooks/ApiWrapperWithTimeout";
import { Const } from "const";
import { TaskDetailPanelNoButton } from "./TaskDetailPanelNoButton";
import { ErrorDialog } from "components/Common/ErrorDialog";
import { useTranslation } from "react-i18next";
import { AwvMapOverlay } from "components/Map/AwvMapOverlay";
import { OfflineAwvMapOverlay } from "components/Map/OfflineAwvMapOverlay";
import { useAwvDataContext } from "context/AwvDataProvider";
import { convertLatLngFromGeolocation } from "utils/Map";

export interface PositioningDialogProps {
  /** Dialog open or not */
  open: boolean;
  awvInfo: AwvInfo;
  task: ResultTaskList;
  /** Action on dialog will close */
  onClose?: () => void;
}

export const PositioningDialog: FC<PositioningDialogProps> = ({
  open,
  awvInfo,
  task,
  onClose,
}) => {
  const { t } = useTranslation();
  const [isTilesLoaded, setIsTilesLoaded] = useState(false);
  const user = useUserContext();
  const { awvData } = useAwvDataContext();
  const { mapData } = useMapDataContext();

  const [getTasksApi] = useApiWithTimeout(
    TasksApi,
    Const.operationTimeout.getTask
  );
  const [reloadPath, setReloadPath] = useState<Array<Array<number>>>([]);
  const [awvs, setAwvs] = useState<AwvInfo[] | undefined>();
  const [isError, setIsError] = useState(false);
  const [errMsg, setErrMsg] = useState("");
  const [startNodeId, setStartNodeId] = useState(-1);
  const [firstNode, setFirstNode] = useState<MapNodeType>();
  const [direction, setDirection] = useState<number>();
  // Threshold value to be regarded as the same coordinates [m]
  const samePointThreshold: number = 0.5;

  const mapProps = useMemo(() => {
    return {
      mapContainerStyle: {
        height: "100%",
        width: "100%",
      },
      options: {
        disableDefaultUI: true,
        mapTypeControl: true,
        zoomControl: true,
        gestureHandling: "greedy",
        keyboardShortcuts: false,
        mapId: process.env.REACT_APP_GOOGLEMAP_MAPID,
      },
      center: {
        lat: user.jobsite?.latitude ?? 0,
        lng: user.jobsite?.longitude ?? 0,
      },
      zoom: user.jobsite?.zoom ?? 11,
      onTilesLoaded: () => {
        setIsTilesLoaded(true);
      },
    };
  }, [user.jobsite]);
  const autoStartNodeIds = task.jobInfos
    ?.filter((node) => node.extensionTimeUnload !== undefined)
    .map((node) => node.unloadingNodeId);
  if (task.extensionTimeLoad !== undefined && task.startNodeId !== undefined) {
    autoStartNodeIds?.unshift(task.startNodeId);
  }
  const isStartNode = (node: MapNodeType) => {
    return node.id === startNodeId;
  };

  const nodes: MapNodeType[] = mapData.mapInfo
    .flatMap((mapInfo) => mapInfo.nodes)
    .filter((elm): elm is Exclude<typeof elm, undefined> => elm !== undefined)
    .filter((node) => isStartNode(node));

  const reloadTasks = useCallback(async () => {
    if (!task.frameTypeAndNumber) {
      return undefined;
    }
    const frameTypeAndNumbers: Array<string> = [task.frameTypeAndNumber];
    // Get Tasks
    try {
      const reTasks = await getTasksApi.getTask({
        frameTypeAndNumberList: frameTypeAndNumbers,
        jobSetId: task.jobSetId,
        mapId: mapData.mapId,
        needsPath: true,
        isApproval: false,
      });
      if (
        !reTasks.taskInfos ||
        reTasks.taskInfos.length === 0 ||
        !reTasks.taskInfos[0].taskActualRoutePaths ||
        reTasks.taskInfos[0].taskActualRoutePaths.length === 0
      ) {
        console.info("tasks is empty.");
        return undefined;
      }
      return reTasks.taskInfos[0];
    } catch (error: unknown) {
      return undefined;
    }
  }, [getTasksApi, mapData.mapId, task.frameTypeAndNumber, task.jobSetId]);

  const calcDirection = (
    point1: google.maps.LatLngLiteral,
    path: number[][]
  ) => {
    if (path.length > 1) {
      console.log("The second point was chosen for calc direction.");
      const point2 = new google.maps.LatLng(path[1][1], path[1][0]);
      if (
        google.maps.geometry.spherical.computeDistanceBetween(point1, point2) <=
        samePointThreshold
      ) {
        console.log(
          "Can not calc direction: The second point was also the same as the start node"
        );
      } else {
        setDirection(
          google.maps.geometry.spherical.computeHeading(point1, point2)
        );
      }
    }
  };

  useEffect(() => {
    if (open) {
      reloadTasks().then((result: ResultTaskList | undefined) => {
        console.log("Reload Result:");
        console.log(result);
        if (result?.taskActualRoutePaths && result?.startNodeId !== undefined) {
          setReloadPath(result.taskActualRoutePaths);
          setStartNodeId(result.startNodeId);
        } else {
          // failed
          const errorMsg = t("AWVUI-MSG177");
          setErrMsg(errorMsg);
          setIsError(true);
        }
      });
    } else {
      setReloadPath([]);
    }
  }, [reloadTasks, t, task, open]);

  useEffect(() => {
    if (nodes.length > 0) {
      setFirstNode(nodes[0]);
    }
  }, [nodes]);

  useEffect(() => {
    if (firstNode && reloadPath.length > 0) {
      const firstPoint = convertLatLngFromGeolocation(firstNode.geolocation);
      const secondPoint = new google.maps.LatLng(
        reloadPath[0][1],
        reloadPath[0][0]
      );
      if (firstPoint) {
        if (
          google.maps.geometry.spherical.computeDistanceBetween(
            firstPoint,
            secondPoint
          ) <= samePointThreshold
        ) {
          calcDirection(firstPoint, reloadPath);
        } else {
          setDirection(
            google.maps.geometry.spherical.computeHeading(
              firstPoint,
              secondPoint
            )
          );
        }
      }
    }
    console.log("direction = " + direction?.toString() + " [deg]");
  }, [firstNode, reloadPath, t, direction]);

  useEffect(() => {
    if (awvData && awvInfo) {
      const awv = awvData.awvInfos?.find(
        (info) => info.frameTypeAndNumber === awvInfo.frameTypeAndNumber
      );
      if (awv) {
        setAwvs([awv]);
      } else {
        setAwvs(undefined);
      }
    }
  }, [awvData, awvInfo]);

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth={false}
        fullWidth
        PaperProps={{ sx: { height: "100%" } }}
      >
        <DialogContainer sidePanelWidth="350px">
          <MapArea>
            <Map {...mapProps}>
              {isTilesLoaded && (
                <>
                  <ResetMapPosition
                    center={mapProps.center}
                    zoom={mapProps.zoom}
                  />
                  <ResetMapHeading />
                </>
              )}
              <NodeMapOverlay mapData={mapData} />
              {direction !== undefined &&
                reloadPath.length > 0 &&
                firstNode && (
                  <>
                    <AutoPositioningCircleMapOverlay
                      firstNode={firstNode}
                      paths={reloadPath}
                      direction={direction}
                    />
                  </>
                )}
              <AwvMapOverlay awvInfos={{ awvInfos: awvs }} />
              <OfflineAwvMapOverlay awvInfos={{ awvInfos: awvs }} />
            </Map>
          </MapArea>
          <DetailArea>
            <TaskDetailPanelNoButton task={task} onClose={onClose} />
          </DetailArea>
        </DialogContainer>
        <ErrorDialog
          open={isError}
          onPositiveButtonClick={() => {
            setIsError(false);
            onClose?.();
          }}
          message={errMsg}
          positiveButtonText={t("AWVUI-MSG22")}
        />
      </Dialog>
    </>
  );
};
